import React from "react";
import { Link } from "react-router-dom";
import {
    buildImageUrl,
    getCommentSpotPhoto,
    getSpotPreviewPhoto,
    getSuggestionPhoto,
    placeName,
    spotName,
    spotTitle,
} from "../../utils/spotDisplay";

function SpotThumb({ photo, className = "" }) {
    const src = buildImageUrl(photo);
    return (
        <span className={`profile-spot-thumb${className ? ` ${className}` : ""}`}>
            {src ? <img src={src} alt="" /> : <span className="profile-spot-thumb__placeholder" aria-hidden>📍</span>}
        </span>
    );
}

function StatusBadge({ label, tone = "neutral" }) {
    return <span className={`profile-status profile-status--${tone}`}>{label}</span>;
}

function statusTone(status) {
    if (status === "published" || status === "approved") return "success";
    if (status === "rejected" || status === "discarded") return "danger";
    if (status === "draft") return "muted";
    return "warning";
}

export function ProfileListMeta({ total, currentCount, tabKey, currentPage, totalPages, onPageChange, pageSize = 20 }) {
    return (
        <div className="profile-list__meta">
            <p>Всего: <strong>{total}</strong> · На странице: <strong>{currentCount}</strong></p>
            {total > pageSize && (
                <div className="profile-list__pager">
                    <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => onPageChange(tabKey, Math.max(1, currentPage - 1))}
                        disabled={currentPage <= 1}
                    >
                        Назад
                    </button>
                    <span>{currentPage} / {totalPages}</span>
                    <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => onPageChange(tabKey, Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage >= totalPages}
                    >
                        Вперёд
                    </button>
                </div>
            )}
        </div>
    );
}

function SpotEngagementStats({ spot }) {
    const comments = Number(spot.commentsCount ?? 0);
    const likes = Number(spot.likesCount ?? 0);
    const favorites = Number(spot.favoritesCount ?? 0);
    return (
        <div className="profile-spot-engagement">
            <span className="profile-popular-spot__stat">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                {comments}
            </span>
            <span className="profile-popular-spot__stat">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {likes}
            </span>
            <span className="profile-popular-spot__stat">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
                {favorites}
            </span>
        </div>
    );
}

export function ProfileMySpotsList({ spots, statusLabel, normalizeStatus, showStats = true }) {
    return (
        <ul className="profile-my-spots">
            {spots.map((spot) => {
                const status = normalizeStatus ? normalizeStatus(spot) : null;
                const water = placeName(spot);
                const hasAside = (status && statusLabel) || showStats;
                return (
                    <li key={spot.id}>
                        <Link className="profile-my-spots__item profile-list-card" to={`/spots/${spot.id}`}>
                            <SpotThumb photo={getSpotPreviewPhoto(spot)} className="profile-spot-thumb--row" />
                            <div className="profile-my-spots__main">
                                <strong className="profile-my-spots__title">{spotName(spot)}</strong>
                                {water ? <span className="profile-my-spots__place">{water}</span> : null}
                            </div>
                            {hasAside && (
                                <div className="profile-list-card__aside">
                                    {status && statusLabel ? (
                                        <StatusBadge label={statusLabel(status)} tone={statusTone(status)} />
                                    ) : null}
                                    {showStats && <SpotEngagementStats spot={spot} />}
                                </div>
                            )}
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
}

export function ProfileSpotTiles({ spots }) {
    return (
        <div className="profile-spots-grid">
            {spots.map((spot) => {
                const water = placeName(spot);
                return (
                    <Link key={spot.id} className="profile-spot-tile" to={`/spots/${spot.id}`}>
                        <span className="profile-spot-tile__media">
                            <SpotThumb photo={getSpotPreviewPhoto(spot)} className="profile-spot-thumb--tile" />
                        </span>
                        <span className="profile-spot-tile__body">
                            <strong className="profile-spot-tile__title">{spotName(spot)}</strong>
                            {water ? <span className="profile-spot-tile__place">{water}</span> : null}
                        </span>
                    </Link>
                );
            })}
        </div>
    );
}

export function ProfileCommentsList({ comments, commentStatusLabel, formatDate }) {
    return (
        <ul className="profile-comments-list">
            {comments.map((item) => {
                const text = item.comment || item.text || "Без текста";
                const water = item.spot?.placeName || "";
                const spotLabel = spotTitle(item.spot) || (item.spotId ? `Точка #${item.spotId}` : "");
                const content = (
                    <>
                        <SpotThumb photo={getCommentSpotPhoto(item)} className="profile-spot-thumb--row" />
                        <div className="profile-list-card__main">
                            {water ? <span className="profile-comments-list__place">{water}</span> : null}
                            {!water && spotLabel ? (
                                <span className="profile-comments-list__place">{spotLabel}</span>
                            ) : null}
                            <p className="profile-comments-list__text">{text}</p>
                        </div>
                        <div className="profile-list-card__aside">
                            <StatusBadge label={commentStatusLabel(item.status)} tone={statusTone(item.status)} />
                            <time>{formatDate(item.createdAt)}</time>
                        </div>
                    </>
                );

                return (
                    <li key={item.id}>
                        {item.spotId ? (
                            <Link className="profile-comments-list__item profile-list-card" to={`/spots/${item.spotId}`}>
                                {content}
                            </Link>
                        ) : (
                            <div className="profile-comments-list__item profile-list-card profile-comments-list__item--static">
                                {content}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

export function ProfileBlogCommentsList({ comments, commentStatusLabel, formatDate }) {
    return (
        <ul className="profile-comments-list">
            {comments.map((item) => {
                const text = item.comment || item.text || "Без текста";
                const articleTitle = item.blog?.title || (item.blogId ? `Статья #${item.blogId}` : "");
                const articleSlug = item.blog?.slug;
                const content = (
                    <>
                        <span className="profile-spot-thumb profile-spot-thumb--row">
                            {item.blog?.image ? (
                                <img src={buildImageUrl(item.blog.image)} alt="" />
                            ) : (
                                <span className="profile-spot-thumb__placeholder" aria-hidden>📰</span>
                            )}
                        </span>
                        <div className="profile-list-card__main">
                            {articleTitle ? (
                                <span className="profile-comments-list__place">{articleTitle}</span>
                            ) : null}
                            <p className="profile-comments-list__text">{text}</p>
                        </div>
                        <div className="profile-list-card__aside">
                            <StatusBadge label={commentStatusLabel(item.status)} tone={statusTone(item.status)} />
                            <time>{formatDate(item.createdAt)}</time>
                        </div>
                    </>
                );

                return (
                    <li key={item.id}>
                        {articleSlug ? (
                            <Link className="profile-comments-list__item profile-list-card" to={`/blog/${articleSlug}`}>
                                {content}
                            </Link>
                        ) : (
                            <div className="profile-comments-list__item profile-list-card profile-comments-list__item--static">
                                {content}
                            </div>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

export function ProfileSuggestionsList({ suggestions, statusLabel, suggestionStatus, formatDate }) {
    return (
        <ul className="profile-suggestions-list">
            {suggestions.map((item) => {
                const status = suggestionStatus(item);
                const water = placeName(item);
                return (
                    <li key={item.id}>
                        <Link
                            className="profile-suggestions-list__item profile-list-card"
                            to={`/profile/suggestions/${item.id}`}
                        >
                            <SpotThumb photo={getSuggestionPhoto(item)} className="profile-spot-thumb--row" />
                            <div className="profile-list-card__main">
                                {water ? <span className="profile-suggestions-list__place">{water}</span> : null}
                                <strong className="profile-suggestions-list__title">
                                    {item.title || `Предложение #${item.id}`}
                                </strong>
                            </div>
                            <div className="profile-list-card__aside">
                                <StatusBadge label={statusLabel(status)} tone={statusTone(status)} />
                                <time>{formatDate(item.createdAt || item.updatedAt)}</time>
                            </div>
                        </Link>
                    </li>
                );
            })}
        </ul>
    );
}
